import axios from '@/lib/axios'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { ToastInterface, useToast } from 'vue-toastification'

import { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'

import {
	CreateRatePlanCodeParams,
	RatePlanCode,
	UpdateRatePlanCodeParams,
} from '@/internal/modules/providers/interfaces/RatePlanCode'

interface Props {
	immediate?: boolean
	params?: Params
	config?: Config
}

interface Config {
	errorHandling: boolean
}

interface Params {
	chainId?: string | number
	hotelId?: string | number
}

interface DeleteParams {
	id: number
}

interface RestoreParams {
	id: number
}

/**
 * Obtener todos los códigos rate plan
 */
const getRatePlanCodes = async (
	params?: Params,
	errorHandling = true
): Promise<RatePlanCode[]> => {
	const { data } = await axios.get<RatePlanCode[]>(
		'/api/internal/rate-plan-codes',
		{
			params,
			errorHandling,
		} as CustomAxiosRequestConfig
	)

	return data
}

/**
 * Crear un nuevo código rate plan
 */
const createRatePlanCode = async (
	code: CreateRatePlanCodeParams,
	toast: ToastInterface
) => {
	try {
		const { data } = await axios.post<RatePlanCode>(
			'/api/internal/rate-plan-codes',
			{
				...code,
				gds: 'amadeus',
				serviceType: 'hotel',
			}
		)
		toast.success('Código rate plan creado correctamente')

		return data
	} catch (error: any) {
		toast.error(error.response?.data?.message || 'Error al crear el código')
		throw error
	}
}

/**
 * Actualizar un código rate plan existente
 */
const updateRatePlanCode = async (
	code: UpdateRatePlanCodeParams,
	toast: ToastInterface
) => {
	try {
		const { data } = await axios.put<RatePlanCode>(
			`/api/internal/rate-plan-codes/${code.id}`,
			{
				code: code.code,
				gds: 'amadeus',
				serviceType: 'hotel',
				type: code.type,
				hotelId: code.hotelId,
				chainId: code.chainId,
			}
		)
		toast.success('Código rate plan actualizado correctamente')

		return data
	} catch (error: any) {
		toast.error(
			error.response?.data?.message || 'Error al actualizar el código'
		)
		throw error
	}
}

/**
 * Eliminar un código rate plan
 */
const deleteRatePlanCode = async (
	params: DeleteParams,
	toast: ToastInterface
) => {
	try {
		await axios.delete(`/api/internal/rate-plan-codes/${params.id}`)
		toast.success('Código rate plan eliminado correctamente')
	} catch (error: any) {
		toast.error(error.response?.data?.message || 'Error al eliminar el código')
		throw error
	}
}

/**
 * Restaurar un código rate plan eliminado
 */
const restoreRatePlanCode = async (
	params: RestoreParams,
	toast: ToastInterface
) => {
	try {
		const { data } = await axios.post<RatePlanCode>(
			`/api/internal/rate-plan-codes/${params.id}/restore`
		)
		toast.success('Código rate plan restaurado correctamente')

		return data
	} catch (error: any) {
		toast.error(error.response?.data?.message || 'Error al restaurar el código')
		throw error
	}
}

/**
 * Composable para gestionar códigos rate plan
 * Maneja operaciones CRUD para códigos a nivel general (no anidados en hotels)
 */
const useRatePlanCodes = (props: Props = {}) => {
	const toast = useToast()

	const { config, params } = props
	const { errorHandling = true } = config || {}

	// Query para obtener la lista de códigos
	const { data, isLoading, isError, refetch, isRefetching } = useQuery(
		['rate-plan-codes', params?.chainId, params?.hotelId],
		() => getRatePlanCodes(params, errorHandling),
		{ enabled: props.immediate }
	)

	// Mutation para crear código
	const createRatePlanCodeMutation = useMutation(
		(code: CreateRatePlanCodeParams) => createRatePlanCode(code, toast)
	)

	// Mutation para actualizar código
	const updateRatePlanCodeMutation = useMutation(
		(code: UpdateRatePlanCodeParams) => updateRatePlanCode(code, toast)
	)

	// Mutation para eliminar código
	const deleteRatePlanCodeMutation = useMutation((params: DeleteParams) =>
		deleteRatePlanCode(params, toast)
	)

	// Mutation para restaurar código
	const restoreRatePlanCodeMutation = useMutation((params: RestoreParams) =>
		restoreRatePlanCode(params, toast)
	)

	return {
		// Data
		ratePlanCodes: data,

		// Estados
		isLoading,
		isRefetching,
		isError,

		// Acciones
		refetch,

		// Create
		createRatePlanCode: createRatePlanCodeMutation.mutate,
		createRatePlanCodeAsync: createRatePlanCodeMutation.mutateAsync,
		createRatePlanCodeLoading: createRatePlanCodeMutation.isLoading,

		// Update
		updateRatePlanCode: updateRatePlanCodeMutation.mutate,
		updateRatePlanCodeAsync: updateRatePlanCodeMutation.mutateAsync,
		updateRatePlanCodeLoading: updateRatePlanCodeMutation.isLoading,

		// Delete
		deleteRatePlanCode: deleteRatePlanCodeMutation.mutate,
		deleteRatePlanCodeAsync: deleteRatePlanCodeMutation.mutateAsync,
		deleteRatePlanCodeLoading: deleteRatePlanCodeMutation.isLoading,

		// Restore
		restoreRatePlanCode: restoreRatePlanCodeMutation.mutate,
		restoreRatePlanCodeAsync: restoreRatePlanCodeMutation.mutateAsync,
		restoreRatePlanCodeLoading: restoreRatePlanCodeMutation.isLoading,
	}
}

export default useRatePlanCodes
